import {
  deleteShowroom,
  readShowroomState,
  refreshFromPage,
  reloadWhenReady,
  showroomRequest,
  showroomToast,
  updateShowroomState,
} from "./showroom-api";

export { readShowroomState };

// One delegated listener so refresh-swapped markup needs no re-binding; after a
// write the page is re-fetched until it shows the change (see showroom-api.js).

const REGIONS = [
  "[data-showroom-state]",
  "#showroom-panel-party",
  "#showroom-panel-lineup",
  "#showroom-modal-export [data-export-canvas]",
  // Its minimum is the member count.
  "#showroom-modal-size [data-showroom-form]",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (email = "") => email.trim().toLowerCase();

class ShowroomPage extends HTMLElement {
  connectedCallback() {
    this.showroomId = this.dataset.showroomId;
    this.lineupLimit = Number(this.dataset.lineupLimit) || 3;

    this.addEventListener("click", (event) => this.handleClick(event));
    this.addEventListener("change", (event) => this.handleChange(event));
    this.addEventListener("submit", (event) => this.handleSubmit(event));
    this.addEventListener("input", (event) => this.updateFormState(event.target.closest("form")));
    this.addEventListener("keydown", (event) => this.handleTabKeydown(event));

    this.nav = this.querySelector(".showroom__nav");
    this.navToggle = this.querySelector("[data-showroom-nav-toggle]");
    this.navToggle?.addEventListener("click", () => this.toggleNav());

    this.syncLineupButtons();
    document.addEventListener("showroom:refreshed", () => this.syncLineupButtons());

    // Forms opened straight from a data-showroom-modal-open trigger (e.g. party
    // size) get their "Not finalized yet" state applied as they open.
    this.addEventListener("showroom-modal:open", (event) => {
      const form = event.target.querySelector("[data-showroom-form='size']");
      if (!form) return;

      this.syncNotFinalized(form);
      this.updateFormState(form);
    });
    // The lineup slider is re-rendered by refreshes; remember who was
    // selected so the new one can pick them again.
    this.addEventListener("showroom:lineup-member-selected", (event) => {
      this.selectedLineupMemberId = event.detail?.memberId || "";
      this.syncLineupButtons();
    });

    window.addEventListener("popstate", () => this.showTab(this.getTabFromUrl(), { updateUrl: false }));
  }

  get state() {
    return readShowroomState(this);
  }

  request(endpoint, body) {
    return showroomRequest(endpoint, { body, showroom: this.showroomId });
  }

  // Tabs ---------------------------------------------------------------

  getTabFromUrl() {
    const tab = new URLSearchParams(window.location.search).get("active_section");

    return ["party", "lineup"].includes(tab) ? tab : "home";
  }

  // Mobile: the tabs open as a dropdown from the "Showroom Home ▾" toggle.
  toggleNav(open = !this.nav?.classList.contains("is-open")) {
    this.nav?.classList.toggle("is-open", open);
    this.navToggle?.setAttribute("aria-expanded", String(open));
  }

  showTab(name, { updateUrl = true, focus = false } = {}) {
    this.querySelectorAll("[role=tab][data-showroom-tab]").forEach((tab) => {
      const selected = tab.dataset.showroomTab === name;

      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });

    this.querySelectorAll("[data-showroom-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.showroomPanel !== name;
    });

    this.toggleNav(false);

    this.dispatchEvent(new CustomEvent("showroom:tab-changed", { bubbles: true, detail: { name } }));

    if (!updateUrl) return;

    const url = new URL(window.location.href);
    if (name === "home") {
      url.searchParams.delete("active_section");
    } else {
      url.searchParams.set("active_section", name);
    }
    window.history.pushState({}, "", url);
  }

  handleTabKeydown(event) {
    const tab = event.target.closest("[role=tab][data-showroom-tab]");
    if (!tab || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    const tabs = [...this.querySelectorAll("[role=tab][data-showroom-tab]")];
    const index = tabs.indexOf(tab);
    let next = index;

    if (event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = tabs.length - 1;

    event.preventDefault();
    this.showTab(tabs[next].dataset.showroomTab, { focus: true });
  }

  // Delegated actions --------------------------------------------------

  handleClick(event) {
    const target = event.target.closest(
      "[data-showroom-tab], [data-showroom-add-member], [data-showroom-send-invite], [data-showroom-send-reminder], [data-showroom-edit-member], [data-showroom-remove-member], [data-showroom-edit-event], [data-showroom-add-to-lineup], [data-showroom-remove-from-lineup], [data-showroom-delete]",
    );
    if (!target || target.disabled) return;

    const { dataset } = target;

    if ("showroomAddMember" in dataset) {
      // The modal itself opens from data-showroom-modal-open; this only
      // numbers its heading after the seat that was clicked.
      const heading = document.querySelector("#showroom-modal-add-member [data-add-member-heading]");
      if (heading && dataset.index) heading.textContent = heading.dataset.template.replace("[index]", dataset.index);
    } else if ("showroomTab" in dataset) {
      this.showTab(dataset.showroomTab);
      if (!target.matches("[role=tab]")) this.scrollIntoView({ behavior: "smooth" });
    } else if ("showroomSendInvite" in dataset) {
      this.sendInvite(target, "sendInviteToShowroom", this.dataset.localeInviteSent);
    } else if ("showroomSendReminder" in dataset) {
      this.sendInvite(target, "sendInvitationReminder", this.dataset.localeReminderSent);
    } else if ("showroomEditMember" in dataset) {
      this.openEditMember(target);
    } else if ("showroomRemoveMember" in dataset) {
      this.removeMember(target);
    } else if ("showroomEditEvent" in dataset) {
      this.openEditEvent(dataset.showroomEditEvent, target);
    } else if ("showroomAddToLineup" in dataset) {
      this.addToLineup(target);
    } else if ("showroomRemoveFromLineup" in dataset) {
      event.stopPropagation();
      this.removeFromLineup(target);
    } else if ("showroomDelete" in dataset) {
      // Sends them on to their next showroom (see deleteShowroom).
      this.setBusy(target, true);
      deleteShowroom(dataset.showroomDelete).finally(() => this.setBusy(target, false));
    }
  }

  handleChange(event) {
    if (event.target.matches("[data-showroom-assign]")) {
      this.toggleAssign(event.target);
      return;
    }

    if (event.target.matches("[data-not-finalized]")) {
      this.syncNotFinalized(event.target.closest("[data-event-field], [data-showroom-form]"));
    }

    this.updateFormState(event.target.closest("form"));
  }

  handleSubmit(event) {
    const form = event.target.closest("[data-showroom-form]");
    if (!form) return;

    event.preventDefault();

    const handlers = {
      "add-member": () => this.submitAddMember(form),
      "edit-member": () => this.submitEditMember(form),
      event: () => this.submitEvent(form),
      size: () => this.submitSize(form),
    };

    handlers[form.dataset.showroomForm]?.();
  }

  // Busy / error helpers ------------------------------------------------

  setBusy(element, busy) {
    if (!element) return;

    element.classList.toggle("is-loading", busy);
    element.toggleAttribute("aria-busy", busy);
    if ("disabled" in element) element.disabled = busy;
  }

  showFormError(form, message) {
    const error = form?.querySelector("[data-form-error]");
    if (!error) return showroomToast(message);

    error.textContent = message || "";
    error.hidden = !message;
  }

  closeModalFor(element) {
    element?.closest("showroom-modal")?.close();
  }

  refreshRegions(isReady) {
    return refreshFromPage(REGIONS, { isReady: (doc) => isReady(readShowroomState(doc)) });
  }

  // Invites -------------------------------------------------------------

  async sendInvite(button, endpoint, successMessage) {
    const email = normalizeEmail(button.dataset.email);
    const memberId = button.dataset.memberId || "";

    this.setBusy(button, true);
    const { ok, error } = await this.request(endpoint, { email, member_id: memberId });
    this.setBusy(button, false);

    if (!ok) {
      showroomToast(error || this.dataset.localeGenericError);
      return;
    }

    showroomToast(successMessage?.replace("[email]", email));

    if (endpoint === "sendInviteToShowroom") {
      await this.refreshRegions((state) =>
        state.members?.some((member) => (memberId ? member.id === memberId : member.email === email) && member.invited),
      );
    }
  }

  // Members -------------------------------------------------------------

  updateFormState(form) {
    if (!form?.matches("[data-showroom-form]")) return;

    const submit = form.querySelector("[data-form-submit]");
    if (!submit) return;

    const type = form.dataset.showroomForm;

    if (type === "add-member") {
      submit.disabled = !form.elements.name.value.trim() || !form.elements.role.value;
    } else if (type === "event") {
      submit.disabled = !this.isEventFieldDirty(form);
    } else if (type === "size") {
      const notFinalized = this.getNotFinalized(form);
      submit.disabled =
        !this.isNotFinalizedChanged(form) &&
        (notFinalized?.checked || form.elements.size.value === form.dataset.initialValue);
    }
  }

  validateMemberForm(form) {
    const name = form.elements.name.value.trim();
    const email = normalizeEmail(form.elements.email.value);

    if (!name) return { error: form.elements.name.placeholder };
    if (email && !EMAIL_PATTERN.test(email)) return { error: this.dataset.localeInvalidEmail };

    return { name, email, role: form.elements.role.value };
  }

  async submitAddMember(form) {
    const values = this.validateMemberForm(form);
    if (values.error) return this.showFormError(form, values.error);

    const submit = form.querySelector("[data-form-submit]");
    const expectedCount = (this.state.members?.length || 0) + 1;

    this.showFormError(form, "");
    this.setBusy(submit, true);

    // A member without an email gets a placeholder address from the
    // showroom app so they can still hold a place in the lineup.
    const body = { name: values.name, role: values.role };
    body.email = values.email || `no-email-${window.handleize(values.name)}-${Date.now()}@revelryemail.com`;

    const { ok, error } = await this.request("addMemberToShowroom", body);

    if (!ok) {
      this.setBusy(submit, false);
      this.showFormError(form, error || this.dataset.localeGenericError);
      return;
    }

    // Saved: close now; the new member's card arrives with the refresh.
    this.setBusy(submit, false);
    form.reset();
    this.updateFormState(form);
    this.closeModalFor(form);

    await this.refreshRegions((state) => (state.members?.length || 0) >= expectedCount);
  }

  openEditMember(button) {
    const modal = document.getElementById("showroom-modal-edit-member");
    const form = modal?.querySelector("[data-showroom-form='edit-member']");
    if (!form) return;

    const heading = form.querySelector("[data-edit-member-heading]");
    if (heading) heading.textContent = heading.dataset.template.replace("[index]", button.dataset.index);

    form.elements.originalEmail.value = button.dataset.originalEmail;
    form.elements.memberId.value = button.dataset.memberId || "";
    form.elements.name.value = button.dataset.name;
    form.elements.email.value = button.dataset.email;
    form.elements.email.readOnly = "emailLocked" in button.dataset;
    const lockedHint = form.querySelector("[data-email-locked-hint]");
    if (lockedHint) lockedHint.hidden = !form.elements.email.readOnly;
    form.elements.role.value = button.dataset.role;

    form.dataset.removable = button.dataset.removable;
    const remove = form.querySelector("[data-showroom-remove-member]");
    if (remove) remove.hidden = button.dataset.removable !== "true";

    this.showFormError(form, "");
    modal.open(button);
  }

  async submitEditMember(form) {
    const values = this.validateMemberForm(form);
    if (values.error) return this.showFormError(form, values.error);

    const originalEmail = form.elements.originalEmail.value;
    const memberId = form.elements.memberId.value;
    const email = values.email || originalEmail;
    const submit = form.querySelector("[data-form-submit]");

    this.showFormError(form, "");
    this.setBusy(submit, true);

    const { ok, error } = await this.request("updateShowroomMember", {
      originalEmail,
      member_id: memberId,
      name: values.name,
      email,
      role: values.role,
    });

    if (!ok) {
      this.setBusy(submit, false);
      this.showFormError(form, error || this.dataset.localeGenericError);
      return;
    }

    // Saved: close now; the updated card arrives with the refresh.
    this.setBusy(submit, false);
    this.closeModalFor(form);

    await this.refreshRegions((state) =>
      state.members?.some(
        (member) => (memberId ? member.id === memberId : member.email === email) && member.name === values.name,
      ),
    );
  }

  async removeMember(button) {
    const form = button.closest("form");
    const email = form?.elements.originalEmail.value;
    const memberId = form?.elements.memberId.value || "";
    // The owner's own record is never removable.
    if (form?.dataset.removable !== "true") return;
    if (!email || !window.confirm(this.dataset.localeConfirmRemoveMember)) return;

    this.setBusy(button, true);
    const { ok, error } = await this.request("removeMemberFromShowroom", { email, member_id: memberId });

    if (!ok) {
      this.setBusy(button, false);
      this.showFormError(form, error || this.dataset.localeGenericError);
      return;
    }

    // Removed: take their card, lineup column and styles off the page now.
    this.setBusy(button, false);
    this.closeModalFor(form);
    if (memberId) {
      this.querySelectorAll(`[data-member-id="${CSS.escape(memberId)}"]`).forEach((element) =>
        (element.closest("li") || element).remove(),
      );
      updateShowroomState((state) => ({
        ...state,
        members: (state.members || []).filter((member) => member.id !== memberId),
        lineup: (state.lineup || []).filter((item) => item.member !== memberId),
      }));
      this.syncLineupButtons();
    }

    await this.refreshRegions((state) =>
      memberId
        ? !state.members?.some((member) => member.id === memberId)
        : !state.members?.some((member) => member.email === normalizeEmail(email)),
    );
  }

  // Event details -------------------------------------------------------

  openEditEvent(field, trigger) {
    const modal = document.getElementById("showroom-modal-event-field");
    const form = modal?.querySelector("[data-showroom-form='event']");
    if (!form) return;

    form.dataset.activeField = field;
    form.querySelectorAll("[data-event-field]").forEach((fieldset) => {
      const active = fieldset.dataset.eventField === field;

      fieldset.hidden = !active;
      fieldset.disabled = !active;
    });

    this.showFormError(form, "");
    this.syncNotFinalized(this.getActiveFieldset(form));
    this.updateFormState(form);
    modal.open(trigger);
  }

  getActiveFieldset(form) {
    return form.querySelector(`[data-event-field="${form.dataset.activeField}"]`);
  }

  // A detail's "Not finalized yet" checkbox (snippet.showroom-not-finalized).
  getNotFinalized(container) {
    return container?.querySelector("[data-not-finalized]") || null;
  }

  isNotFinalizedChanged(container) {
    const checkbox = this.getNotFinalized(container);
    return !!checkbox && String(checkbox.checked) !== checkbox.dataset.initialChecked;
  }

  // While "Not finalized yet" is checked the detail's own inputs are off.
  syncNotFinalized(container) {
    const checkbox = this.getNotFinalized(container);
    if (!checkbox) return;

    container.querySelectorAll("input, textarea, button, select").forEach((control) => {
      if (control === checkbox || control.closest("[data-form-submit]")) return;
      control.disabled = checkbox.checked;
    });
    container.querySelectorAll("showroom-color-picker, quantity-stepper, date-field").forEach((el) => {
      el.classList.toggle("is-disabled", checkbox.checked);
    });
  }

  isEventFieldDirty(form) {
    const fieldset = this.getActiveFieldset(form);
    if (!fieldset) return false;

    const notFinalized = this.getNotFinalized(fieldset);
    if (this.isNotFinalizedChanged(fieldset)) return true;
    if (notFinalized?.checked) return false;

    const roles = fieldset.querySelector(".showroom-roles");
    if (roles) {
      const checked = roles.querySelector("input:checked")?.value || "";
      return !!checked && checked !== roles.dataset.initialValue;
    }

    const picker = fieldset.querySelector("showroom-color-picker");
    if (picker) return picker.isDirty && picker.value.length > 0;

    return [...fieldset.querySelectorAll("input[data-initial-value], textarea[data-initial-value]")].some(
      (input) => input.value.trim() !== (input.dataset.initialValue || "").trim(),
    );
  }

  // Builds the request for the visible field: { endpoint, body, isReady },
  // or { error } when the field is invalid.
  getEventPayload(form) {
    const field = form.dataset.activeField;
    const fieldset = this.getActiveFieldset(form);
    const notFinalized = !!this.getNotFinalized(fieldset)?.checked;
    const { elements } = form;

    if (field === "role") {
      const role = fieldset.querySelector(".showroom-roles input:checked")?.value;
      const email = normalizeEmail(elements.owner_email.value);
      if (!role) return { error: this.dataset.localeGenericError };

      return {
        endpoint: "updateShowroomMember",
        body: { originalEmail: email, email, name: elements.owner_name.value, role },
        isReady: (state) => state.members?.some((member) => member.email === email && member.role === role),
      };
    }

    if (field === "names") {
      const bride = elements.bride.value.trim();
      const partner = elements.partner.value.trim();
      if (!bride || !partner) return { error: elements.bride.placeholder };

      return { body: { bride, partner }, isReady: (state) => state.bride === bride && state.partner === partner };
    }

    if (field === "date") {
      if (notFinalized) {
        return { body: { date_not_finalized_yet: "true" }, isReady: (state) => !state.date };
      }

      const dateField = form.querySelector("date-field");
      const date = dateField?.parseInputDate();
      if (!date || dateField.isBeforeMin(date))
        return { error: dateField?.querySelector(".date-field__error")?.textContent };

      const iso = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
      const day = iso.slice(0, 10);

      return {
        body: { date: iso, date_not_finalized_yet: "false" },
        isReady: (state) => String(state.date || "").startsWith(day),
      };
    }

    if (field === "location") {
      // There's no "not finalized" flag for the location: an empty location is
      // how "not set" is stored, so clearing both fields empties it.
      const city = elements.city.value.trim();
      const stateName = elements.state.value.trim();
      if (!city && !stateName) return { body: { location: "" }, isReady: (state) => !state.location };
      if (!city || !stateName) return { error: elements.city.placeholder };

      const location = `${city}, ${stateName}`;
      return { body: { location }, isReady: (state) => state.location === location };
    }

    if (field === "colors") {
      if (notFinalized) {
        return {
          body: { colors: [], color_not_finalized_yet: "true" },
          isReady: (state) => !(state.colors || []).length,
        };
      }

      const colors = form.querySelector("showroom-color-picker")?.value || [];
      if (!colors.length) return { error: this.dataset.localeGenericError };

      return {
        body: { colors, color_not_finalized_yet: "false" },
        isReady: (state) => (state.colors || []).join(",") === colors.join(","),
      };
    }

    if (field === "message") {
      const message = elements.message.value.trim();
      return { body: { message }, isReady: (state) => (state.message || "") === message };
    }

    return { error: this.dataset.localeGenericError };
  }

  async submitEvent(form) {
    const payload = this.getEventPayload(form);
    if (payload.error !== undefined) return this.showFormError(form, payload.error || this.dataset.localeGenericError);

    await this.submitShowroomUpdate(form, payload);
  }

  async submitSize(form) {
    if (this.getNotFinalized(form)?.checked) {
      await this.submitShowroomUpdate(form, {
        body: { size_not_finalized_yet: "true" },
        isReady: (state) => !Number(state.size),
      });
      return;
    }

    const size = String(parseInt(form.elements.size.value, 10) || 0);

    await this.submitShowroomUpdate(form, {
      body: { size, size_not_finalized_yet: "false" },
      isReady: (state) => String(state.size) === size,
    });
  }

  // The hero, nav and modals all show event details, so once the change is
  // visible the page reloads rather than patching each place.
  async submitShowroomUpdate(form, { endpoint = "updateShowroom", body, isReady }) {
    const submit = form.querySelector("[data-form-submit]");

    this.showFormError(form, "");
    this.setBusy(submit, true);

    const { ok, error } = await this.request(endpoint, body);

    if (!ok) {
      this.setBusy(submit, false);
      this.showFormError(form, error || this.dataset.localeGenericError);
      return;
    }

    const shown = await reloadWhenReady((doc) => isReady(readShowroomState(doc)));
    if (!shown) {
      this.setBusy(submit, false);
      this.closeModalFor(form);
    }
  }

  // Lineup --------------------------------------------------------------

  // Lineup actions target a member by id: one email can hold more than one
  // seat, so the email alone can't say whose lineup it is.
  getActiveLineupMember() {
    const slider = this.querySelector("showroom-lineup-slider");
    return { memberId: slider?.activeMemberId || "", email: slider?.activeEmail || "" };
  }

  getLineupTarget(button) {
    if ("lineupPicker" in button.dataset) return this.getActiveLineupMember();
    return { memberId: button.dataset.memberId || "", email: normalizeEmail(button.dataset.email) };
  }

  getLineupCount(memberId) {
    return (this.state.lineup || []).filter((item) => item.member === memberId).length;
  }

  // Paints every "Add to lineup" button: in the lineup already, lineup full,
  // or available. Picker buttons (the owner's style panel) follow whichever
  // member is selected in the lineup.
  syncLineupButtons() {
    this.querySelectorAll("[data-showroom-add-to-lineup]").forEach((button) => {
      const { memberId } = this.getLineupTarget(button);
      const lineupMembers =
        button.dataset.lineupMembers || button.closest("[data-lineup-members]")?.dataset.lineupMembers || "";
      const inLineup = !!memberId && lineupMembers.includes(`,${memberId},`);
      const isFull = !inLineup && !!memberId && this.getLineupCount(memberId) >= this.lineupLimit;

      button.classList.toggle("is-in-lineup", inLineup);
      button.classList.toggle("is-full", isFull);
      button.setAttribute("aria-pressed", String(inLineup));
      // A style needs a variant or (imported ones) at least its product.
      button.disabled = !memberId || isFull || !(button.dataset.variantId || button.dataset.productId);

      const addLabel = button.querySelector(".showroom-closet-card__lineup-add");
      if (addLabel) {
        addLabel.dataset.defaultLabel ??= addLabel.textContent.trim();
        addLabel.textContent = isFull ? this.dataset.localeLineupFull : addLabel.dataset.defaultLabel;
      }
    });
  }

  async addToLineup(button) {
    if (button.classList.contains("is-in-lineup")) return;

    const { memberId, email } = this.getLineupTarget(button);
    const variant = button.dataset.variantId || "";
    const product = button.dataset.productId || "";
    if (!memberId || !(variant || product)) return;

    if (this.getLineupCount(memberId) >= this.lineupLimit) {
      showroomToast(this.dataset.localeLineupFullAlert);
      this.syncLineupButtons();
      return;
    }

    // What the page showed before: the app may put the member's existing
    // size of this dress in the lineup rather than this exact variant.
    const countBefore = this.getLineupCount(memberId);

    this.setBusy(button, true);
    // With no variant the app adds the product's first available size.
    const { ok, error } = await this.request("addProductVariantToLineup", {
      variant: String(variant),
      product: String(product),
      email,
      member_id: memberId,
    });

    if (!ok) {
      this.setBusy(button, false);

      if (String(error).includes("CUSTOMER_LINEUP_LIMIT")) {
        showroomToast(this.dataset.localeLineupFullAlert);
      } else if (String(error).includes("SHOWROOM_LINEUP_LIMIT")) {
        showroomToast(this.dataset.localeShowroomLineupFull);
      } else {
        showroomToast(this.dataset.localeGenericError);
      }
      return;
    }

    // Saved: count it and mark the button now; the column fills on refresh.
    updateShowroomState((state) => ({
      ...state,
      lineup: [...(state.lineup || []), { member: memberId, email, variant: String(variant) }],
    }));
    const members = button.dataset.lineupMembers || ",";
    button.dataset.lineupMembers = members.includes(`,${memberId},`) ? members : `${members}${memberId},`;
    const card = button.closest("[data-lineup-members]");
    if (card && card !== button && !card.dataset.lineupMembers.includes(`,${memberId},`)) {
      card.dataset.lineupMembers += `${memberId},`;
    }
    this.setBusy(button, false);
    this.syncLineupButtons();

    await this.refreshRegions(
      (state) =>
        state.lineup?.some((item) => item.member === memberId && String(item.variant) === String(variant)) ||
        (state.lineup || []).filter((item) => item.member === memberId).length > countBefore,
    );
  }

  async removeFromLineup(button) {
    const { memberId, email } = this.getLineupTarget(button);
    const variant = String(button.dataset.variantId);
    const product = button.dataset.productId || "";
    const card = button.closest("[data-lineup-card]");

    card?.classList.add("is-loading");
    this.setBusy(button, true);

    const { ok } = await this.request("removeProductVariantFromLineup", {
      variant,
      product,
      email,
      member_id: memberId,
    });

    if (!ok) {
      card?.classList.remove("is-loading");
      this.setBusy(button, false);
      showroomToast(this.dataset.localeGenericError);
      return;
    }

    // Removed: take the style out of the column now.
    this.removeLineupSlide(card, button.closest("[data-slide]"));
    updateShowroomState((state) => ({
      ...state,
      lineup: (state.lineup || []).filter((item) => !(item.member === memberId && String(item.variant) === variant)),
    }));
    this.querySelectorAll("[data-lineup-members]").forEach((element) => {
      const holder = element.closest("[data-variant-id]") || element;
      if (String(holder.dataset.variantId) === variant) {
        element.dataset.lineupMembers = element.dataset.lineupMembers.replace(`,${memberId},`, ",");
      }
    });
    this.syncLineupButtons();

    await this.refreshRegions(
      (state) => !state.lineup?.some((item) => item.member === memberId && String(item.variant) === variant),
    );
  }

  // Drops one style from a member's lineup column; the last one leaves the
  // numbered empty frame the column shows when it has no styles.
  removeLineupSlide(card, slide) {
    if (!card || !slide) return;

    card.classList.remove("is-loading");
    const slides = card.querySelector("showroom-lineup-slides");
    slide.remove();

    const remaining = card.querySelectorAll("[data-slide]").length;
    if (remaining > 0) {
      slides?.refresh?.();
      return;
    }
    if (!slides) return;

    const index = [...card.parentElement.children].indexOf(card) + 1;
    const name = card.dataset.memberName || "";
    const frame = document.createElement("button");
    frame.type = "button";
    frame.className = "showroom-lineup-card__frame";
    frame.dataset.lineupCardToggle = "";
    frame.setAttribute("aria-label", name);
    frame.innerHTML = `<span class="showroom-lineup-card__index body-large">${index}</span>`;

    const label = document.createElement("p");
    label.className = "showroom-lineup-card__name showroom-lineup-card__name--full h4";
    label.textContent = name;

    slides.replaceWith(frame, label);
    card.classList.add("showroom-lineup-card--placeholder");
  }

  async toggleAssign(input) {
    const assigned = input.checked;
    const { memberId, email } = this.getLineupTarget(input);
    const slide = input.closest("[data-slide]");

    if (assigned) this.openAssignConfirmation(input, slide);

    input.disabled = true;
    const { ok } = await this.request("toggleAssignLineupItem", {
      email,
      member_id: memberId,
      variant: String(input.dataset.variantId),
      assigned: String(assigned),
    });
    input.disabled = false;

    if (!ok) {
      input.checked = !assigned;
      document.getElementById("showroom-modal-assign")?.close();
      showroomToast(this.dataset.localeGenericError);
    }
  }

  openAssignConfirmation(input, slide) {
    const modal = document.getElementById("showroom-modal-assign");
    if (!modal || !slide) return;

    const card = slide.closest("[data-lineup-card]");
    const title = modal.querySelector("[data-assign-title]");
    const image = slide.querySelector("img");
    const color = modal.querySelector("[data-assign-color]");

    title.textContent = title.dataset.template.replace("[name]", card?.dataset.memberName || "");
    modal.querySelector("[data-assign-product]").textContent = slide.dataset.productTitle || "";
    modal.querySelector("[data-assign-image]").replaceChildren(...(image ? [image.cloneNode()] : []));

    color.replaceChildren();
    if (slide.dataset.colorImage) {
      const swatch = document.createElement("img");
      swatch.className = "showroom-swatch__image";
      swatch.src = slide.dataset.colorImage;
      swatch.alt = "";
      color.append(swatch);
    }
    const label = document.createElement("span");
    label.className = "showroom-swatch__label";
    label.textContent = slide.dataset.colorLabel || "";
    color.append(label);

    modal.open(input);
  }
}

if (!customElements.get("showroom-page")) {
  customElements.define("showroom-page", ShowroomPage);
}
